import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

export default function BadLeadReport() {
  const { id } = useParams();
  const nav = useNavigate();
  const [enums, setEnums] = useState({ reasons: [], resolutions: [] });
  const [lead, setLead] = useState(null);
  const [returnWindow, setReturnWindow] = useState(null);
  const [form, setForm] = useState({
    reason: "", notes: "", requested_resolution: "review_only",
    date_discovered: new Date().toISOString().slice(0, 10),
    confirmed_accurate: false, sub_reason: "",
  });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/bad-leads/_meta/enums").then((r) => setEnums(r.data));
    api.get(`/leads/${id}`).then((r) => {
      setLead(r.data.lead);
      setReturnWindow(r.data.return_window || null);
    });
  }, [id]);

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/files/upload?kind=evidence&lead_id=${id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFiles((prev) => [...prev, data]);
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.confirmed_accurate) return toast.error("Confirm accuracy first");
    if (!form.reason) return toast.error("Select a reason");
    setSubmitting(true);
    try {
      const payload = {
        lead_id: id,
        reason: form.reason,
        sub_reason: form.sub_reason || null,
        notes: form.notes,
        requested_resolution: form.requested_resolution,
        date_discovered: form.date_discovered,
        confirmed_accurate: form.confirmed_accurate,
        evidence_file_ids: files.map((f) => f.id),
      };
      const { data } = await api.post("/bad-leads", payload);
      toast.success("Bad-lead report submitted");
      nav(`/bad-leads/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!lead) return <div className="label-mono">Loading…</div>;

  return (
    <div className="max-w-3xl">
      <div className="label-mono">Report bad lead</div>
      <h1 className="font-chivo font-black text-3xl uppercase tracking-tighter">Quality report</h1>
      <div className="mt-2 text-sm">
        Lead: <span className="font-mono">{lead.id.slice(0, 8)}</span> —{" "}
        <b>{lead.first_name} {lead.last_name}</b>
      </div>

      {returnWindow && (
        <div className={`brut-card p-3 mt-3 text-sm ${
          returnWindow.can_report ? "border-emerald-700 bg-emerald-50" : "border-red-700 bg-red-50"
        }`} data-testid="return-window-banner">
          <div className="label-mono">Return window</div>
          {returnWindow.can_report ? (
            <div>
              <b>{Math.floor((returnWindow.hours_left || 0) / 24)} days,{" "}
              {Math.round((returnWindow.hours_left || 0) % 24)} hours</b> left to submit a return
              (total window: {returnWindow.days} days from delivery).
            </div>
          ) : (
            <div className="text-red-800">
              <b>Return window closed.</b> This lead is older than the {returnWindow.days}-day
              return window and can no longer be reported.
            </div>
          )}
        </div>
      )}

      <form onSubmit={submit} className="brut-card mt-6 p-6 space-y-5">
        <div>
          <label className="label-mono">Reason*</label>
          <select data-testid="bl-reason" required value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            className="w-full border border-black px-3 py-2 mt-1">
            <option value="">— select —</option>
            {enums.reasons.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </div>

        {form.reason === "other" && (
          <div>
            <label className="label-mono">Sub-reason (required when reason = other)</label>
            <input data-testid="bl-subreason" required value={form.sub_reason}
              onChange={(e) => setForm({ ...form, sub_reason: e.target.value })}
              className="w-full border border-black px-3 py-2 mt-1" />
          </div>
        )}

        <div>
          <label className="label-mono">Detailed notes*</label>
          <textarea data-testid="bl-notes" required rows={4} value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full border border-black px-3 py-2 mt-1" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label-mono">Date discovered</label>
            <input data-testid="bl-date" type="date" value={form.date_discovered}
              onChange={(e) => setForm({ ...form, date_discovered: e.target.value })}
              className="w-full border border-black px-3 py-2 mt-1" />
          </div>
          <div>
            <label className="label-mono">Requested resolution</label>
            <select data-testid="bl-resolution" value={form.requested_resolution}
              onChange={(e) => setForm({ ...form, requested_resolution: e.target.value })}
              className="w-full border border-black px-3 py-2 mt-1">
              {enums.resolutions.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="label-mono">Evidence (screenshots, docs)</label>
          <input data-testid="bl-file" type="file"
            onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
            disabled={uploading}
            className="mt-1 block w-full file:border file:border-black file:px-3 file:py-1.5 file:mr-3 file:uppercase file:text-[11px] file:tracking-widest file:bg-white" />
          {files.length > 0 && (
            <ul className="mt-2 text-xs font-mono space-y-1">
              {files.map((f) => (
                <li key={f.id} className="flex justify-between border-b border-zinc-200 py-1">
                  <span className="truncate">{f.original_filename}</span>
                  <span className="text-zinc-500">{Math.round((f.size || 0) / 1024)} KB</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <label className="inline-flex items-start gap-2 text-sm">
          <input data-testid="bl-confirm" type="checkbox" checked={form.confirmed_accurate}
            onChange={(e) => setForm({ ...form, confirmed_accurate: e.target.checked })} />
          <span>I confirm the information provided is accurate to the best of my knowledge.</span>
        </label>

        <div className="flex justify-end gap-2 pt-3 border-t border-black">
          <button type="button" onClick={() => nav(-1)}
            className="border border-black px-3 py-2 text-xs uppercase tracking-widest">Cancel</button>
          <button type="submit" data-testid="bl-submit"
            disabled={submitting || (returnWindow && !returnWindow.can_report)}
            className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100 disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? "Submitting…" : (returnWindow && !returnWindow.can_report ? "Return window closed" : "Submit report")}
          </button>
        </div>
      </form>
    </div>
  );
}
